/**
 * =========================================================
 * Social Media Hub - Premium Dashboard
 * =========================================================
 *
 * Modern, sleek dashboard with premium visual design,
 * smooth animations, and polished user experience.
 */

// @mui material components
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import PropTypes from "prop-types";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

import { useEffect, useState } from "react";
import { fetchRSSContentMultipleStatuses, fetchPosts } from "api/supabase";

// Color gradients for visual enhancement
const gradients = {
  info: "linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)",
  warning: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)",
  success: "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
  error: "linear-gradient(135deg, #ef4444 0%, #f87171 100%)",
};

function Dashboard() {
  // State for metrics
  const [rssCount, setRssCount] = useState(0);
  const [draftCount, setDraftCount] = useState(0);
  const [postedCount, setPostedCount] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Fetch metrics function extracted for reuse
  const fetchMetrics = async () => {
    try {
      // RSS Content: New + Processing
      const rssItems = await fetchRSSContentMultipleStatuses(["New", "Processing"]);
      setRssCount(rssItems.length);

      // Drafts (Pending Approval)
      const drafts = await fetchPosts("Pending Approval");
      setDraftCount(drafts.length);

      // Posted
      const posted = await fetchPosts("Posted");
      setPostedCount(posted.length);

      // Rejected
      const rejected = await fetchPosts("Rejected");
      setRejectedCount(rejected.length);

      // Recent posts (last 5, any status)
      const allRecent = [...drafts, ...posted, ...rejected]
        .sort((a, b) => new Date(b.created_at || b.date) - new Date(a.created_at || a.date))
        .slice(0, 5);
      setRecentPosts(allRecent);
    } catch (e) {
      console.error("Error fetching metrics:", e);
    }
  };

  // Manual refresh handler for pull-to-refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchMetrics();
    setIsRefreshing(false);
  };

  useEffect(() => {
    // iOS Safari viewport fix
    if (/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream) {
      document.body.style.webkitTouchCallout = "none";
      document.body.style.WebkitOverflowScrolling = "touch";
      document.body.style.overflowX = "hidden";
      // Prevent zoom on double tap
      document.addEventListener(
        "touchstart",
        function (event) {
          if (event.touches.length > 1) {
            event.preventDefault();
          }
        },
        { passive: false }
      );
    }

    async function initialFetch() {
      setLoading(true);
      await fetchMetrics();
      setLoading(false);
    }
    initialFetch();
  }, []);

  // Status badge component
  const StatusBadge = ({ status }) => {
    const getStatusStyles = () => {
      switch (status) {
        case "Posted":
          return {
            bg: "#10b98115",
            color: "#10b981",
            icon: "check_circle",
          };
        case "Rejected":
          return {
            bg: "#ef444415",
            color: "#ef4444",
            icon: "cancel",
          };
        case "Pending Approval":
          return {
            bg: "#f59e0b15",
            color: "#f59e0b",
            icon: "pending",
          };
        default:
          return {
            bg: "#6366f115",
            color: "#6366f1",
            icon: "info",
          };
      }
    };

    const styles = getStatusStyles();

    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          px: 1.5,
          py: 0.5,
          borderRadius: "8px",
          backgroundColor: styles.bg,
        }}
      >
        <Icon sx={{ fontSize: "0.9rem", color: styles.color }}>{styles.icon}</Icon>
        <MDTypography
          variant="caption"
          fontWeight="600"
          sx={{ color: styles.color, fontSize: "0.75rem" }}
        >
          {status}
        </MDTypography>
      </Box>
    );
  };

  StatusBadge.propTypes = {
    status: PropTypes.string.isRequired,
  };

  // Platform badge component
  const PlatformBadge = ({ post }) => {
    const platform = post.linkedin_post_url
      ? { name: "LinkedIn", color: "#0a66c2", icon: "work" }
      : post.x_post_url
      ? { name: "X", color: "#1da1f2", icon: "close" }
      : post.threads_post_url
      ? { name: "Threads", color: "#000000", icon: "chat" }
      : null;

    if (!platform) return <MDTypography variant="caption">-</MDTypography>;

    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          px: 1,
          py: 0.3,
          borderRadius: "6px",
          backgroundColor: `${platform.color}10`,
        }}
      >
        <Icon sx={{ fontSize: "0.8rem", color: platform.color }}>{platform.icon}</Icon>
        <MDTypography
          variant="caption"
          fontWeight="500"
          sx={{ color: platform.color, fontSize: "0.7rem" }}
        >
          {platform.name}
        </MDTypography>
      </Box>
    );
  };

  PlatformBadge.propTypes = {
    post: PropTypes.shape({
      linkedin_post_url: PropTypes.string,
      x_post_url: PropTypes.string,
      threads_post_url: PropTypes.string,
    }).isRequired,
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Header Section */}
        <MDBox mb={4}>
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
          >
            <MDBox>
              <MDTypography
                variant="h4"
                fontWeight="700"
                sx={{
                  color: "#0f172a",
                  fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
                  letterSpacing: "-0.02em",
                }}
              >
                Welcome back! 👋
              </MDTypography>
              <MDTypography
                variant="body2"
                sx={{
                  color: "#64748b",
                  fontSize: "0.9rem",
                  mt: 0.5,
                }}
              >
                Here&apos;s what&apos;s happening with your content today.
              </MDTypography>
            </MDBox>
            {/* Refresh Button for Mobile */}
            <MDButton
              variant="outlined"
              color="info"
              size="small"
              onClick={handleRefresh}
              disabled={isRefreshing}
              startIcon={
                <Icon
                  sx={{
                    animation: isRefreshing ? "spin 1s linear infinite" : "none",
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                  }}
                >
                  refresh
                </Icon>
              }
              sx={{
                minHeight: "40px",
                borderRadius: "12px",
                fontWeight: 600,
                textTransform: "none",
                display: { xs: "flex", md: "none" },
              }}
            >
              {isRefreshing ? "Refreshing..." : "Refresh"}
            </MDButton>
          </MDBox>
        </MDBox>

        {/* Statistics Cards */}
        <Grid container spacing={{ xs: 2, sm: 3 }} mb={4}>
          <Grid item xs={6} md={6} lg={3}>
            <ComplexStatisticsCard
              color="info"
              icon="rss_feed"
              title="RSS Items"
              count={loading ? "-" : rssCount}
              percentage={{
                color: "info",
                amount: "",
                label: "New & Processing",
              }}
            />
          </Grid>
          <Grid item xs={6} md={6} lg={3}>
            <ComplexStatisticsCard
              color="warning"
              icon="edit_note"
              title="Drafts"
              count={loading ? "-" : draftCount}
              percentage={{
                color: "warning",
                amount: "",
                label: "Pending Approval",
              }}
            />
          </Grid>
          <Grid item xs={6} md={6} lg={3}>
            <ComplexStatisticsCard
              color="success"
              icon="check_circle"
              title="Posted"
              count={loading ? "-" : postedCount}
              percentage={{
                color: "success",
                amount: "",
                label: "Published",
              }}
            />
          </Grid>
          <Grid item xs={6} md={6} lg={3}>
            <ComplexStatisticsCard
              color="error"
              icon="cancel"
              title="Rejected"
              count={loading ? "-" : rejectedCount}
              percentage={{
                color: "error",
                amount: "",
                label: "Rejected Posts",
              }}
            />
          </Grid>
        </Grid>

        {/* Recent Activity Section */}
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12} md={12} lg={8}>
            <Card
              sx={{
                borderRadius: "20px",
                border: "1px solid rgba(0, 0, 0, 0.04)",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.04)",
                overflow: "hidden",
              }}
            >
              <MDBox p={3} pb={2}>
                <MDBox display="flex" alignItems="center" justifyContent="space-between">
                  <MDBox>
                    <MDTypography
                      variant="h6"
                      fontWeight="600"
                      sx={{ color: "#0f172a", letterSpacing: "-0.01em" }}
                    >
                      Recent Content Activity
                    </MDTypography>
                    <MDTypography variant="caption" sx={{ color: "#64748b" }}>
                      Latest updates from your content pipeline
                    </MDTypography>
                  </MDBox>
                  <Icon sx={{ color: "#94a3b8" }}>article</Icon>
                </MDBox>
              </MDBox>
              <MDBox
                sx={{
                  overflowX: "auto",
                  WebkitOverflowScrolling: "touch",
                  "&::-webkit-scrollbar": {
                    height: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "#f1f5f9",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#cbd5e1",
                    borderRadius: "10px",
                  },
                }}
              >
                <MDBox
                  component="table"
                  width="100%"
                  sx={{ borderCollapse: "collapse", minWidth: { xs: "600px", md: "100%" } }}
                >
                  <MDBox component="thead">
                    <MDBox component="tr" sx={{ backgroundColor: "#f8fafc" }}>
                      <MDBox
                        component="th"
                        sx={{
                          textAlign: "left",
                          p: 2,
                          fontWeight: 600,
                          color: "#64748b",
                          fontSize: "0.75rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                        }}
                      >
                        Title
                      </MDBox>
                      <MDBox
                        component="th"
                        sx={{
                          textAlign: "left",
                          p: 2,
                          fontWeight: 600,
                          color: "#64748b",
                          fontSize: "0.75rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                        }}
                      >
                        Status
                      </MDBox>
                      <MDBox
                        component="th"
                        sx={{
                          textAlign: "left",
                          p: 2,
                          fontWeight: 600,
                          color: "#64748b",
                          fontSize: "0.75rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                        }}
                      >
                        Date
                      </MDBox>
                      <MDBox
                        component="th"
                        sx={{
                          textAlign: "left",
                          p: 2,
                          fontWeight: 600,
                          color: "#64748b",
                          fontSize: "0.75rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                        }}
                      >
                        Platform
                      </MDBox>
                    </MDBox>
                  </MDBox>
                  <MDBox component="tbody">
                    {recentPosts.length === 0 && (
                      <MDBox component="tr">
                        <MDBox component="td" colSpan={4} sx={{ p: 4, textAlign: "center" }}>
                          <MDBox
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Icon sx={{ fontSize: "2.5rem", color: "#cbd5e1" }}>inbox</Icon>
                            <MDTypography variant="body2" color="text">
                              {loading ? "Loading..." : "No recent activity."}
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                      </MDBox>
                    )}
                    {recentPosts.map((post) => (
                      <MDBox
                        component="tr"
                        key={post.id}
                        sx={{
                          borderBottom: "1px solid #f1f5f9",
                          transition: "background-color 0.2s",
                          "&:hover": {
                            backgroundColor: "#f8fafc",
                          },
                          "&:last-child": {
                            borderBottom: "none",
                          },
                        }}
                      >
                        <MDBox component="td" sx={{ p: 2 }}>
                          <MDTypography
                            variant="body2"
                            fontWeight="500"
                            sx={{
                              maxWidth: "300px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              color: "#0f172a",
                            }}
                          >
                            {post.title || post.sourceHeadline || "Untitled"}
                          </MDTypography>
                        </MDBox>
                        <MDBox component="td" sx={{ p: 2 }}>
                          <StatusBadge status={post.status} />
                        </MDBox>
                        <MDBox component="td" sx={{ p: 2 }}>
                          <MDTypography variant="caption" sx={{ color: "#64748b" }}>
                            {post.created_at
                              ? new Date(post.created_at).toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                })
                              : "-"}
                          </MDTypography>
                        </MDBox>
                        <MDBox component="td" sx={{ p: 2 }}>
                          <PlatformBadge post={post} />
                        </MDBox>
                      </MDBox>
                    ))}
                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          {/* Quick Actions Panel */}
          <Grid item xs={12} md={12} lg={4}>
            <Card
              sx={{
                borderRadius: "20px",
                border: "1px solid rgba(0, 0, 0, 0.04)",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.04)",
                p: 3,
                height: "100%",
              }}
            >
              <MDTypography
                variant="h6"
                fontWeight="600"
                sx={{ color: "#0f172a", letterSpacing: "-0.01em", mb: 3 }}
              >
                Quick Actions
              </MDTypography>
              <MDBox display="flex" flexDirection="column" gap={2}>
                <MDButton
                  variant="outlined"
                  color="info"
                  fullWidth
                  startIcon={<Icon>article</Icon>}
                  sx={{
                    justifyContent: "flex-start",
                    borderRadius: "12px",
                    py: 1.5,
                    textTransform: "none",
                    fontWeight: 500,
                    "&:hover": {
                      backgroundColor: "#6366f108",
                    },
                  }}
                >
                  View Content Hub
                </MDButton>
                <MDButton
                  variant="outlined"
                  color="warning"
                  fullWidth
                  startIcon={<Icon>edit_note</Icon>}
                  sx={{
                    justifyContent: "flex-start",
                    borderRadius: "12px",
                    py: 1.5,
                    textTransform: "none",
                    fontWeight: 500,
                    "&:hover": {
                      backgroundColor: "#f59e0b08",
                    },
                  }}
                >
                  Review Drafts
                </MDButton>
                <MDButton
                  variant="outlined"
                  color="success"
                  fullWidth
                  startIcon={<Icon>calendar_month</Icon>}
                  sx={{
                    justifyContent: "flex-start",
                    borderRadius: "12px",
                    py: 1.5,
                    textTransform: "none",
                    fontWeight: 500,
                    "&:hover": {
                      backgroundColor: "#10b98108",
                    },
                  }}
                >
                  Open Calendar
                </MDButton>
                <MDButton
                  variant="outlined"
                  color="secondary"
                  fullWidth
                  startIcon={<Icon>chat</Icon>}
                  sx={{
                    justifyContent: "flex-start",
                    borderRadius: "12px",
                    py: 1.5,
                    textTransform: "none",
                    fontWeight: 500,
                    "&:hover": {
                      backgroundColor: "#64748b08",
                    },
                  }}
                >
                  Messages
                </MDButton>
              </MDBox>

              {/* Stats Summary */}
              <MDBox mt={4} pt={3} sx={{ borderTop: "1px solid #f1f5f9" }}>
                <MDTypography
                  variant="caption"
                  fontWeight="600"
                  sx={{ color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}
                >
                  Content Overview
                </MDTypography>
                <MDBox mt={2}>
                  <MDBox display="flex" justifyContent="space-between" mb={1.5}>
                    <MDTypography variant="body2" sx={{ color: "#64748b" }}>
                      Total Items
                    </MDTypography>
                    <MDTypography variant="body2" fontWeight="600" sx={{ color: "#0f172a" }}>
                      {rssCount + draftCount + postedCount + rejectedCount}
                    </MDTypography>
                  </MDBox>
                  <MDBox display="flex" justifyContent="space-between" mb={1.5}>
                    <MDTypography variant="body2" sx={{ color: "#64748b" }}>
                      Approval Rate
                    </MDTypography>
                    <MDTypography variant="body2" fontWeight="600" sx={{ color: "#10b981" }}>
                      {draftCount + postedCount + rejectedCount > 0
                        ? Math.round(
                            (postedCount / (draftCount + postedCount + rejectedCount)) * 100
                          )
                        : 0}
                      %
                    </MDTypography>
                  </MDBox>
                  <MDBox display="flex" justifyContent="space-between">
                    <MDTypography variant="body2" sx={{ color: "#64748b" }}>
                      Active Drafts
                    </MDTypography>
                    <MDTypography variant="body2" fontWeight="600" sx={{ color: "#f59e0b" }}>
                      {draftCount}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
